import { c as _c } from "react/compiler-runtime";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AfterDateValueConfigKind, BeforeDateValueConfigKind, BetweenDateValueConfigKind, CohortFilterGranularity, InTheLastValueConfigKind, TimeRangeConfig } from '@/api/generated/model';
import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { FILTERS } from '@/models/common/filters';
import { formatToHuman } from '@/utils/date.util';
import { nullableNumberToString } from '@/utils/numbers.util';
import { ChartDateRange } from '@/domains/chart/components/create-chart/create-chart-settings/chart-date-range';
import SpecialFilterPopover from '@/components/filters/special-filter-popver';
import { FilterContentV2NestedFilterRow } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2NestedFilterRow';
import { FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS } from '@/comps/FilterPicker/filter-content-v2/constants';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { useProjectsContext } from '@/providers/projects-provider';
import { CalendarDays, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { Input } from '@/comps/primitives/input';
import { Button } from '@/comps/primitives/button';
import { List, ListItem } from '@/comps/list';
import { cn } from '@/utils/shad';
import { SpecialTimeFilterState, SpecialTimeKind } from '@/comps/FilterPicker/filter-content-v2/specialTimeFilterAdapter';
import { useAliasedKindName } from '@/contexts/alias';
import { translate } from '@/utils/translations';
const NESTED_POPOVER_CONTENT_CLASS = cn(FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS, 'border-zinc-800 bg-[#09090B]');
type Props = {
  kind: SpecialTimeKind;
  initialState: SpecialTimeFilterState;
  onChange: (state: SpecialTimeFilterState) => void;
  onApply: (state: SpecialTimeFilterState) => void;
  /** Time range for the additional-filter value fetch. Defaults to the global
   * filters time range, but callers (e.g. the chart builder) should pass the
   * surrounding filter component's time range so it matches its other fetches. */
  timeRange?: TimeRangeConfig;
};
const GRANULARITY_OPTIONS: {
  value: CohortFilterGranularity;
  label: string;
}[] = [{
  value: CohortFilterGranularity.day,
  label: 'days'
}, {
  value: CohortFilterGranularity.week,
  label: 'weeks'
}, {
  value: CohortFilterGranularity.month,
  label: 'months'
}];
const ADDITIONAL_FILTER_FIELD_KINDS = [FILTERS.columnTopic, FILTERS.columnIntent];
function cloneSpecialTimeState(state: SpecialTimeFilterState): SpecialTimeFilterState {
  return {
    fields: {
      ...state.fields
    },
    additionalFilter: state.additionalFilter ? {
      ...state.additionalFilter,
      values: [...(state.additionalFilter.values ?? [])]
    } : undefined,
    preservedOffset: state.preservedOffset
  };
}
export function FilterContentV2SpecialTimeValuePopover(t0) {
  const $ = _c(49);
  const {
    kind,
    initialState,
    onChange,
    onApply,
    timeRange
  } = t0;
  let t1;
  if ($[0] !== initialState) {
    t1 = () => cloneSpecialTimeState(initialState);
    $[0] = initialState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [state, setState] = useState(t1);
  const [rangePopoverOpen, setRangePopoverOpen] = useState(false);
  const [addFilterOpen, setAddFilterOpen] = useState(false);
  let t2;
  if ($[2] !== initialState.fields.filter) {
    t2 = () => initialState.fields.filter ? [initialState.fields.filter] : [];
    $[2] = initialState.fields.filter;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [filters, setFilters] = useState(t2);
  const onChangeRef = useRef(onChange);
  const {
    timeRange: globalTimeRange
  } = useGlobalFiltersContext();
  const effectiveTimeRange = timeRange ?? globalTimeRange;
  const {
    selectedProject
  } = useProjectsContext();
  const projectId = selectedProject?.id;
  const aliasKindName = useAliasedKindName();
  let t3;
  if ($[4] !== aliasKindName) {
    t3 = ADDITIONAL_FILTER_FIELD_KINDS.map(kind_0 => ({
      value: kind_0,
      label: aliasKindName(kind_0) ?? translate("filter", kind_0)
    }));
    $[4] = aliasKindName;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const additionalFilterOptions = t3;
  let t4;
  let t5;
  if ($[6] !== onChange) {
    t4 = () => {
      onChangeRef.current = onChange;
    };
    t5 = [onChange];
    $[6] = onChange;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] !== state) {
    t6 = () => {
      onChangeRef.current(state);
    };
    t7 = [state];
    $[9] = state;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[12] !== filters || $[13] !== kind) {
    t8 = () => {
      setState(current => ({
        ...current,
        fields: {
          ...current.fields,
          kind,
          filter: filters.length > 0 ? filters[0] : null
        },
        additionalFilter: filters.length > 0 ? undefined : current.additionalFilter
      }));
    };
    t9 = [filters, kind];
    $[12] = filters;
    $[13] = kind;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  if ($[16] !== kind || $[17] !== state.fields.filter?.column) {
    t10 = () => (kind === AfterDateValueConfigKind.after || kind === BeforeDateValueConfigKind.before) && Boolean(state.fields.filter?.column);
    $[16] = kind;
    $[17] = state.fields.filter?.column;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  state.fields.filter?.column;
  const hasFilterAsValue = t10();
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = patch => {
      setState(current_0 => ({
        ...current_0,
        fields: {
          ...current_0.fields,
          ...patch
        }
      }));
    };
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const updateFields = t11;
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = event => {
      const parsed = parseInt(event.target.value, 10);
      updateFields({
        number: Number.isNaN(parsed) ? 0 : parsed
      });
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const handleNumberChange = t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = column => {
      setAddFilterOpen(false);
      setState(current_1 => ({
        ...current_1,
        additionalFilter: {
          column,
          values: [],
          is_exclude: false
        }
      }));
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const handleSelectAdditionalFilter = t13;
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = nextFilter => {
      setState(current_2 => ({
        ...current_2,
        additionalFilter: nextFilter ?? undefined
      }));
    };
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const handleAdditionalFilterChange = t14;
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setState(_temp);
    };
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const handleRemoveAdditionalFilter = t15;
  let t16;
  if ($[24] !== effectiveTimeRange || $[25] !== filters || $[26] !== kind || $[27] !== rangePopoverOpen || $[28] !== state.fields.granularity || $[29] !== state.fields.number || $[30] !== state.fields.pickedDate || $[31] !== state.fields.pickedDates) {
    t16 = () => {
      if (kind === InTheLastValueConfigKind.in_the_last) {
        return <div className="flex items-center gap-2"><Input value={nullableNumberToString(state.fields.number)} type="number" className="h-8 w-14 [appearance:textfield] border-zinc-800 bg-[#09090B] text-xs text-white [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" onChange={handleNumberChange} /><div className="min-w-22"><GranularityDropdown value={state.fields.granularity} onChange={granularity => updateFields({
              granularity
            })} /></div></div>;
      }
      if (kind === AfterDateValueConfigKind.after || kind === BeforeDateValueConfigKind.before) {
        return <SpecialFilterPopover date={state.fields.pickedDate} setDate={pickedDate => updateFields({
          pickedDate,
          filter: null
        })} filters={filters} setFilters={setFilters} timeRange={effectiveTimeRange} />;
      }
      if (kind === BetweenDateValueConfigKind.between) {
        return <Popover open={rangePopoverOpen} onOpenChange={setRangePopoverOpen}><PopoverTrigger asChild={true}><Button type="button" variant="outline" size="sm" className="h-8 border-zinc-800 bg-[#09090B] text-xs text-white"><CalendarDays className="size-4" />{state.fields.pickedDates ? `${formatToHuman(state.fields.pickedDates[0].valueOf())} - ${formatToHuman(state.fields.pickedDates[1].valueOf())}` : "Select dates"}</Button></PopoverTrigger><PopoverContent className={NESTED_POPOVER_CONTENT_CLASS} align="start" side="bottom" avoidCollisions={false} onOpenAutoFocus={_temp2}><ChartDateRange onChange={dates => {
              if (dates) {
                updateFields({
                  pickedDates: [dates[0].toDate(), dates[1].toDate()]
                });
              }
            }} onClose={() => setRangePopoverOpen(false)} /></PopoverContent></Popover>;
      }
      return null;
    };
    $[24] = effectiveTimeRange;
    $[25] = filters;
    $[26] = kind;
    $[27] = rangePopoverOpen;
    $[28] = state.fields.granularity;
    $[29] = state.fields.number;
    $[30] = state.fields.pickedDate;
    $[31] = state.fields.pickedDates;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const renderPrimaryValueFields = t16;
  let t17;
  if ($[33] !== renderPrimaryValueFields) {
    t17 = renderPrimaryValueFields();
    $[33] = renderPrimaryValueFields;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== addFilterOpen || $[36] !== additionalFilterOptions || $[37] !== effectiveTimeRange || $[38] !== hasFilterAsValue || $[39] !== projectId || $[40] !== state.additionalFilter) {
    t18 = !hasFilterAsValue && <><div className="flex items-center gap-2"><div className="h-px flex-1 bg-zinc-800" /><span className="text-[10px] tracking-wide text-zinc-500 uppercase">and</span><div className="h-px flex-1 bg-zinc-800" /></div>{state.additionalFilter ? <FilterContentV2NestedFilterRow filter={state.additionalFilter} onChange={handleAdditionalFilterChange} fieldKinds={ADDITIONAL_FILTER_FIELD_KINDS} projectId={projectId} timeRange={effectiveTimeRange} volumeKind="users" showRemove={true} onRemove={handleRemoveAdditionalFilter} /> : <Popover open={addFilterOpen} onOpenChange={setAddFilterOpen}><PopoverTrigger asChild={true}><Button type="button" variant="outline" size="sm" className="h-8 w-full justify-start border-zinc-800 bg-[#09090B] text-xs text-white"><Plus className="mr-1.5 h-3.5 w-3.5" />Select additional filter</Button></PopoverTrigger><PopoverContent className={cn(NESTED_POPOVER_CONTENT_CLASS, "w-48 p-1")} align="start"><List className="min-w-0">{additionalFilterOptions.map(option => <ListItem key={option.value} className="cursor-pointer rounded-[8px] p-2 text-xs text-white hover:bg-neutral-800" onClick={() => handleSelectAdditionalFilter(option.value)}>{option.label}</ListItem>)}</List></PopoverContent></Popover>}</>;
    $[35] = addFilterOpen;
    $[36] = additionalFilterOptions;
    $[37] = effectiveTimeRange;
    $[38] = hasFilterAsValue;
    $[39] = projectId;
    $[40] = state.additionalFilter;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== onApply || $[43] !== state) {
    t19 = <div className="flex justify-end"><Button type="button" className="h-7 gap-1.5 rounded-base bg-primary/80 px-2 text-[12px] font-semibold text-[#101010] hover:bg-primary/70" onClick={() => onApply(state)}>Apply filter</Button></div>;
    $[42] = onApply;
    $[43] = state;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t17 || $[46] !== t18 || $[47] !== t19) {
    t20 = <div className="w-auto max-w-96 overflow-hidden rounded-md border border-zinc-800 bg-[#09090B] shadow-md"><div className="flex flex-col gap-3 p-3">{t17}{t18}{t19}</div></div>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
}
function _temp2(event_0) {
  return event_0.preventDefault();
}
function _temp(current_3) {
  return {
    ...current_3,
    additionalFilter: undefined
  };
}
function GranularityDropdown(t0) {
  const $ = _c(14);
  const {
    value,
    onChange
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== value) {
    t1 = GRANULARITY_OPTIONS.find(option => option.value === value)?.label ?? `${value}s`;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedLabel = t1;
  let t2;
  if ($[2] !== selectedLabel) {
    t2 = <PopoverTrigger asChild={true}><Button type="button" variant="outline" size="sm" className="h-8 w-full justify-between border-zinc-800 bg-[#09090B] px-2 text-xs text-white">{selectedLabel}</Button></PopoverTrigger>;
    $[2] = selectedLabel;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(NESTED_POPOVER_CONTENT_CLASS, "w-32 p-1");
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onChange || $[6] !== value) {
    t4 = GRANULARITY_OPTIONS.map(option_0 => <ListItem key={option_0.value} className={cn("cursor-pointer rounded-[8px] p-2 text-xs text-white hover:bg-neutral-800", value === option_0.value && "bg-neutral-800")} onClick={() => {
      onChange(option_0.value);
      setOpen(false);
    }}>{option_0.label}</ListItem>);
    $[5] = onChange;
    $[6] = value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <PopoverContent className={t3} align="start"><List className="min-w-0">{t4}</List></PopoverContent>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== open || $[11] !== t2 || $[12] !== t5) {
    t6 = <Popover open={open} onOpenChange={setOpen}>{t2}{t5}</Popover>;
    $[10] = open;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}